/**
 * Shared UI building blocks for the urgent care pillar, city pages and
 * service landing pages. Keeps the wording (and especially the safety
 * disclaimer) consistent across every urgent care surface.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, MessageCircle, ArrowRight, ShieldCheck, Clock,
  Globe, Stethoscope, Plane, FileCheck2, ChevronDown,
} from 'lucide-react';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import {
  URGENT_CONDITIONS, URGENT_SERVICES_EXTRA, URGENT_SERVICES_CAVEAT,
} from '../data/urgentCareSeo';

/* ── Coordination disclaimer ─────────────────────────────────────────── */
export const CoordinationDisclaimer = ({ testId = 'urgent-disclaimer' }) => (
  <div
    className="bg-amber-50/60 border border-amber-100 rounded-2xl p-5 lg:p-6 flex gap-3"
    data-testid={testId}
  >
    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
    <div className="text-sm leading-relaxed">
      <p className="font-bold text-amber-900 mb-1">
        For life-threatening emergencies, contact local emergency services immediately.
      </p>
      <p className="text-amber-900/80">
        In Vietnam dial <strong>115</strong> for an ambulance or <strong>113</strong> for police.
        VietClinics provides urgent, <strong>non-emergency</strong> doctor call-outs — we are
        not an ambulance service or a hospital emergency department.
      </p>
    </div>
  </div>
);

/* ── Trust strip (6 items, consistent across pages) ───────────────────── */
export const UrgentTrustStrip = () => {
  const items = [
    { icon: Clock,       label: '24/7 doctor call-outs' },
    { icon: Globe,       label: 'English-speaking doctors' },
    { icon: Stethoscope, label: 'Licensed local doctors' },
    { icon: ShieldCheck, label: 'Arrives within 60 minutes' },
    { icon: FileCheck2,  label: 'Insurance documents on request' },
    { icon: Plane,       label: 'Hotel, apartment & Airbnb visits' },
  ];
  return (
    <section className="bg-white border-y border-slate-100" data-testid="urgent-trust-strip">
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(({ icon: Icon, label }, i) => (
            <div key={label} className="flex items-center gap-3" data-testid={`urgent-trust-${i + 1}`}>
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: '#DCEFEC' }}
              >
                <Icon className="h-4 w-4 text-[#1A8F8A]" />
              </div>
              <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Long-form intro section ──────────────────────────────────────────── */
export const UrgentIntro = ({ heading, paragraphs = [], testId = 'urgent-intro' }) => {
  if (!paragraphs.length) return null;
  return (
    <section className="py-16 lg:py-20 bg-white" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-4xl">
        {heading && (
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-7">
            {heading}
          </h2>
        )}
        <div className="space-y-5">
          {paragraphs.map((p, idx) => (
            <p key={idx} className="text-slate-600 text-base lg:text-lg leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Card grid (services / what's covered) ───────────────────────────── */
export const UrgentCardGrid = ({ heading, eyebrow, items = [], testId = 'urgent-cards' }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="max-w-2xl mb-10">
          {eyebrow && (
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              {eyebrow}
            </p>
          )}
          {heading && (
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              {heading}
            </h2>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-100 p-6"
              style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
              data-testid={`urgent-card-${i + 1}`}
            >
              <p className="font-extrabold text-slate-900 mb-2 text-base">{it.title}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Step list (how it works) ─────────────────────────────────────────── */
export const UrgentSteps = ({ heading, eyebrow, steps = [], testId = 'urgent-steps' }) => {
  if (!steps.length) return null;
  return (
    <section className="py-16 lg:py-20 bg-white" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="max-w-2xl mb-10">
          {eyebrow && (
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              {eyebrow}
            </p>
          )}
          {heading && (
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              {heading}
            </h2>
          )}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-100 p-6 relative"
              style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
              data-testid={`urgent-step-${i + 1}`}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-extrabold text-sm mb-4"
                style={{ background: '#1FA7A1' }}
              >
                {i + 1}
              </div>
              <p className="font-extrabold text-slate-900 mb-2 text-base">{s.title}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── FAQ accordion ────────────────────────────────────────────────────── */
export const UrgentFaq = ({ heading = 'Frequently asked questions', items = [], testId = 'urgent-faq' }) => {
  const [open, setOpen] = useState(0);
  if (!items.length) return null;
  return (
    <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-8">
          {heading}
        </h2>
        <div className="space-y-3">
          {items.map((f, i) => (
            <details
              key={i}
              open={i === open}
              onToggle={(e) => e.currentTarget.open && setOpen(i)}
              className="group bg-white rounded-xl border border-slate-100 overflow-hidden"
              style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
              data-testid={`urgent-faq-${i + 1}`}
            >
              <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4">
                <span className="font-bold text-slate-900 text-sm md:text-base">{f.q}</span>
                <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180 flex-shrink-0" />
              </summary>
              <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Editorial / E-E-A-T block ────────────────────────────────────────── */
export const UrgentEditorial = ({ editorial, testId = 'urgent-editorial' }) => {
  if (!editorial) return null;
  return (
    <section className="py-12 bg-white border-t border-slate-100" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-4xl">
        <div
          className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8 flex flex-col sm:flex-row gap-5"
          style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
        >
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: '#DCEFEC' }}
          >
            <FileCheck2 className="h-5 w-5 text-[#1A8F8A]" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-1">
              Reviewed by
            </p>
            <p className="font-extrabold text-slate-900 text-base mb-1">
              {editorial.reviewedBy}
              {editorial.role && (
                <span className="text-slate-500 font-semibold"> — {editorial.role}</span>
              )}
            </p>
            {editorial.methodology && (
              <p className="text-sm text-slate-600 leading-relaxed mt-2">
                {editorial.methodology}
              </p>
            )}
            {editorial.lastUpdated && (
              <p className="text-xs text-slate-500 mt-3">
                Last reviewed: <time dateTime={editorial.lastUpdated}>{editorial.lastUpdated}</time>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── CTA band (WhatsApp + Request Treatment + Enquiry) ────────────────── */
export const UrgentCtaBand = ({
  heading = 'Need urgent medical support in Vietnam?',
  subheading = 'Tell us your symptoms and your address — a licensed doctor arrives within 60 minutes, 24/7.',
  primaryCta = { to: '/urgent-medical-care#urgent-sos', label: 'Get a 24/7 Doctor Call-Out' },
  testId = 'urgent-cta-band',
}) => {
  const { contact } = useContactInfo();
  const whatsapp = contact ? getWhatsAppHref(contact, 'Hello, I need urgent medical care coordination in Vietnam.') : null;
  return (
    <section
      className="py-16 lg:py-20 text-white relative overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #1FA7A1 0%, #138F8A 60%, #0B4F65 100%)',
      }}
      data-testid={testId}
    >
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">{heading}</h2>
        <p className="text-white/85 text-lg max-w-2xl mx-auto mb-7">{subheading}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
          <Link
            to={primaryCta.to}
            className="inline-flex items-center justify-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-full px-7 py-4 hover:-translate-y-0.5 transition-all"
            data-testid="urgent-cta-request-treatment"
          >
            {primaryCta.label}
            <ArrowRight className="h-4 w-4" />
          </Link>
          {whatsapp && (
            <a
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/15 backdrop-blur text-white border border-white/30 font-bold rounded-full px-7 py-4 hover:bg-white/25 transition-all"
              data-testid="urgent-cta-whatsapp"
            >
              <MessageCircle className="h-4 w-4" />
              Message us on WhatsApp
            </a>
          )}
        </div>
        <p className="text-xs text-white/70 mt-5">
          For life-threatening emergencies, dial <strong>115</strong> in Vietnam — VietClinics is a non-emergency coordination service.
        </p>
      </div>
    </section>
  );
};

/* ── Inline coverage map (link cluster to cities + services) ──────────── */
export const UrgentCoverageMap = ({
  liveCities = [],
  expandingCities = [],
  enquiryCities = [],
  showServices = true,
  testId = 'urgent-coverage-map',
}) => (
  <section className="py-16 lg:py-20 bg-white" data-testid={testId}>
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="max-w-2xl mb-10">
        <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
          Where we coordinate
        </p>
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
          Urgent care coverage across Vietnam
        </h2>
      </div>

      <div className={`grid gap-6 mb-8 ${enquiryCities.length ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        <div className="bg-[#F0FAF8] rounded-2xl border border-[#DCEFEC] p-6">
          <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-3">
            Live coverage
          </p>
          <ul className="space-y-2">
            {liveCities.map((c) => (
              <li key={c.href}>
                <Link
                  to={c.href}
                  className="font-bold text-slate-900 hover:text-[#1FA7A1] inline-flex items-center gap-1.5"
                  data-testid={`urgent-coverage-live-${c.slug}`}
                >
                  {c.name}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                {c.note && <p className="text-xs text-slate-500 mt-0.5">{c.note}</p>}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3">
            Cities &amp; service areas
          </p>
          <ul className="space-y-2">
            {expandingCities.map((c) => (
              <li key={c.href}>
                <Link
                  to={c.href}
                  className="font-bold text-slate-900 hover:text-[#1FA7A1] inline-flex items-center gap-1.5"
                  data-testid={`urgent-coverage-expanding-${c.slug}`}
                >
                  {c.name}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <p className="text-xs text-slate-500 mt-0.5">
                  24/7 doctor call-outs — a doctor arrives within 60 minutes
                </p>
              </li>
            ))}
          </ul>
        </div>

        {enquiryCities.length > 0 && (
          <div
            className="bg-slate-50 rounded-2xl border border-slate-100 p-6"
            data-testid="urgent-coverage-enquiry-group"
          >
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3">
              Enquiry-based support
            </p>
            <ul className="space-y-2">
              {enquiryCities.map((c) => (
                <li key={c.href}>
                  <Link
                    to={c.href}
                    className="font-bold text-slate-900 hover:text-[#1FA7A1] inline-flex items-center gap-1.5"
                    data-testid={`urgent-coverage-enquiry-${c.slug}`}
                  >
                    {c.name}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showServices && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Service-specific landing pages
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/doctor-to-hotel-vietnam"
              className="inline-flex items-center gap-1.5 bg-[#F0FAF8] hover:bg-[#DCEFEC] text-[#0B4F65] font-bold text-sm rounded-full px-5 py-2.5 transition-colors"
              data-testid="urgent-coverage-link-doctor-to-hotel"
            >
              Doctor-to-Hotel in Vietnam
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/english-speaking-doctor-vietnam"
              className="inline-flex items-center gap-1.5 bg-[#F0FAF8] hover:bg-[#DCEFEC] text-[#0B4F65] font-bold text-sm rounded-full px-5 py-2.5 transition-colors"
              data-testid="urgent-coverage-link-english-speaking-doctor"
            >
              English-Speaking Doctors in Vietnam
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  </section>
);


/* ── Related services cross-links ────────────────────────────────────── */
export const RelatedServices = ({ heading, links = [], testId = 'related-services' }) => (
  <section className="py-14 lg:py-16 bg-white" data-testid={testId}>
    <div className="container mx-auto px-4 max-w-6xl">
      <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-8">
        {heading}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <Link
              key={l.to}
              to={l.to}
              className="group block bg-[#F7FAF9] hover:bg-white rounded-2xl border border-slate-100 p-6 transition-all hover:-translate-y-0.5"
              style={{ boxShadow: '0 8px 24px rgba(15,42,85,0.04)' }}
              data-testid={`${testId}-${l.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
            >
              <span className="w-11 h-11 rounded-xl bg-[#1FA7A1]/10 flex items-center justify-center mb-4">
                {Icon ? <Icon className="h-5 w-5 text-[#1FA7A1]" /> : null}
              </span>
              <h3 className="text-base font-extrabold text-slate-900 leading-snug mb-1.5">{l.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-3">{l.body}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1] group-hover:gap-2.5 transition-all">
                Learn more
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

/* ── Conditions & extra services (additive SEO block) ─────────────────────
   Rendered BELOW existing page content. Adds symptom/treatment keyword
   coverage without touching any existing copy. Coordination-framed only. */
export const UrgentConditionsServices = ({
  eyebrow = 'More ways VietClinics can help',
  conditionsHeading = 'Conditions we can help with',
  servicesHeading = 'Treatments & services we can coordinate',
  conditions = URGENT_CONDITIONS,
  services = URGENT_SERVICES_EXTRA,
  caveat = URGENT_SERVICES_CAVEAT,
  testId = 'urgent-conditions-services',
}) => (
  <section className="py-16 lg:py-20 bg-white border-t border-slate-100" data-testid={testId}>
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="max-w-2xl mb-10">
        {eyebrow && (
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">{eyebrow}</p>
        )}
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
          {conditionsHeading}
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
        {conditions.map((it, i) => (
          <div
            key={it.title}
            className="bg-[#F7FAF9] rounded-2xl border border-slate-100 p-6"
            data-testid={`${testId}-condition-${i + 1}`}
          >
            <p className="font-extrabold text-slate-900 mb-2 text-[15px] leading-snug">{it.title}</p>
            <p className="text-sm text-slate-600 leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>

      <div className="max-w-2xl mb-10">
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
          {servicesHeading}
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((it, i) => (
          <div
            key={it.title}
            className="bg-[#F7FAF9] rounded-2xl border border-slate-100 p-6"
            data-testid={`${testId}-service-${i + 1}`}
          >
            <p className="font-extrabold text-slate-900 mb-2 text-base">{it.title}</p>
            <p className="text-sm text-slate-600 leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>

      {caveat && (
        <p className="text-xs text-slate-500 leading-relaxed mt-8 max-w-3xl" data-testid={`${testId}-caveat`}>
          {caveat}
        </p>
      )}
    </div>
  </section>
);

